import { useCallback, useEffect, useRef } from 'react';

/**
 * How long after the agent goes live to wait for the first sign of life from the audio
 * graph before declaring the microphone dead.
 *
 * Well inside the 60s at which ElevenLabs gives up on a session that has sent it nothing,
 * so the learner hears about it from us rather than watching the agent quietly hang up.
 */
export const MIC_SILENCE_TIMEOUT_MS = 8000;

/**
 * Catches an audio graph that connected but never ran.
 *
 * The graph either runs or it doesn't: once started, `onaudioprocess` fires continuously
 * and silence still produces buffers. So "nothing has come through at all" is a precise
 * statement about the pipeline, not a guess about whether the learner has spoken yet —
 * which is what makes this safe to act on without false-positiving on a quiet room.
 *
 * A suspended AudioContext produces exactly this: nodes connect, nothing throws, and no
 * audio is ever captured. On 2026-08-13 a learner spent a whole session talking into one.
 *
 * @param {object} params
 * @param {() => boolean} params.isPipelineAlive - Whether any audio has come through yet.
 * @param {() => void} params.onSilent - Called once if it never did.
 * @param {number} [params.timeoutMs] - How long to give it.
 * @returns {{ start: () => void, stop: () => void }}
 */
export default function useMicSilenceWatch({
  isPipelineAlive,
  onSilent,
  timeoutMs = MIC_SILENCE_TIMEOUT_MS,
}) {
  const timerRef = useRef(null);
  const isPipelineAliveRef = useRef(isPipelineAlive);
  const onSilentRef = useRef(onSilent);

  // Keep callbacks current without restarting a running watch.
  useEffect(() => {
    isPipelineAliveRef.current = isPipelineAlive;
    onSilentRef.current = onSilent;
  }, [isPipelineAlive, onSilent]);

  const stop = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const start = useCallback(() => {
    stop();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      if (isPipelineAliveRef.current?.()) return;
      onSilentRef.current?.();
    }, timeoutMs);
  }, [stop, timeoutMs]);

  // Never outlive the component that would report it.
  useEffect(() => stop, [stop]);

  return { start, stop };
}
